import type { UserIdentity } from "./types";

/**
 * Recently looked-up users, kept in localStorage so the list survives a reload.
 *
 * PRIVACY. This persists real people's phone numbers on the operator's machine,
 * outliving the session — unlike the ID token, which `api.ts` deliberately keeps
 * in memory only. It is not a credential, so the exposure is different in kind:
 * losing it leaks who was looked at, not access to anything. That is why the
 * entries are trimmed to identity alone (no messages, no session state) and why
 * the view offers a way to clear them.
 */

const KEY = "bean_admin.recent_users";
export const MAX_RECENT = 6;

export interface RecentUser {
  user_id: string;
  handle: string;
  /** epoch ms of the most recent lookup — the sort key. */
  at: number;
}

function isRecent(v: unknown): v is RecentUser {
  const r = v as RecentUser;
  return (
    !!r &&
    typeof r.user_id === "string" &&
    r.user_id.length > 0 &&
    typeof r.handle === "string" &&
    typeof r.at === "number"
  );
}

/**
 * Never throws. localStorage can hold anything a previous version (or another
 * tab, or a person with devtools) put there, and it throws outright in Safari's
 * private mode — a broken list must not take the view down with it.
 */
export function loadRecent(): RecentUser[] {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(KEY) ?? "[]");
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isRecent)
      .sort((a, b) => b.at - a.at)
      .slice(0, MAX_RECENT);
  } catch {
    return [];
  }
}

/** Record a lookup and return the new list. Most recent first, deduped by user. */
export function rememberRecent(identity: UserIdentity): RecentUser[] {
  const entry: RecentUser = {
    user_id: identity.user_id,
    handle: identity.handle || "",
    at: Date.now(),
  };
  // Dedupe on user_id, not the typed query: the same person can be reached as
  // "+16502530000", "(650) 253-0000" or their raw user id, and three chips for
  // one person would crowd out five other people.
  const next = [entry, ...loadRecent().filter((r) => r.user_id !== entry.user_id)]
    .slice(0, MAX_RECENT);
  try {
    localStorage.setItem(KEY, JSON.stringify(next));
  } catch {
    /* private mode / quota — the list still works for this session */
  }
  return next;
}

export function clearRecent(): RecentUser[] {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* nothing to do — the caller drops the list either way */
  }
  return [];
}
